import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import './Guidelines.css';

const Guidelines = () => {
  return (
    <div className="legal-page">
      <Helmet>
        <title>Normas de la Comunidad | Graphyra</title>
        <meta name="description" content="Normas y directrices de comportamiento para la comunidad de Graphyra" />
      </Helmet>

      <div className="legal-container">
        <div className="legal-header">
          <h1 className="legal-title">Normas de la Comunidad</h1>
          <p className="legal-update">Última actualización: 3 de enero de 2026</p>
          <p className="legal-intro">
            Graphyra es un espacio para compartir conocimiento, descubrir perspectivas y conectar con contenido 
            de calidad. Estas normas establecen las expectativas de comportamiento para mantener una comunidad 
            segura, respetuosa y constructiva para todos.
          </p>
          <div className="legal-notice">
            <strong>Nuestra visión:</strong> Construir una comunidad donde todas las voces puedan ser escuchadas 
            en un ambiente de respeto, aprendizaje y colaboración.
          </div>
        </div>

        <nav className="legal-nav">
          <a href="#principios">1. Principios</a>
          <a href="#respeto">2. Respeto</a>
          <a href="#contenido-prohibido">3. Contenido Prohibido</a>
          <a href="#autenticidad">4. Autenticidad</a>
          <a href="#propiedad">5. Propiedad Intelectual</a>
          <a href="#spam">6. Spam</a>
          <a href="#seguridad">7. Seguridad</a>
          <a href="#moderacion">8. Moderación</a>
          <a href="#reportar">9. Reportar</a>
          <a href="#consecuencias">10. Consecuencias</a>
        </nav>

        <div className="legal-content">
          <section id="principios" className="legal-section">
            <h2>1. Principios Fundamentales</h2>
            <p>
              Nuestra comunidad se basa en tres pilares fundamentales que guían todas las interacciones:
            </p>
            <div className="definition-list">
              <div className="definition-item">
                <strong>🤝 Respeto Mutuo</strong>
                <p>
                  Trata a los demás como te gustaría ser tratado. Respeta las opiniones diversas, 
                  incluso cuando no estés de acuerdo.
                </p>
              </div>
              <div className="definition-item">
                <strong>💡 Contribución Constructiva</strong>
                <p>
                  Aporta valor a las conversaciones. Comparte conocimiento, perspectivas únicas y 
                  contenido de calidad que enriquezca la comunidad.
                </p>
              </div>
              <div className="definition-item">
                <strong>🛡️ Seguridad y Bienestar</strong>
                <p>
                  Mantén un espacio seguro para todos. No compartas contenido que pueda dañar, 
                  amenazar o hacer sentir inseguro a ningún miembro de la comunidad.
                </p>
              </div>
            </div>
          </section>

          <section id="respeto" className="legal-section">
            <h2>2. Respeto y Dignidad</h2>
            
            <h3>2.1. Comportamiento Esperado</h3>
            <div className="info-box">
              <h4>✅ SÍ está permitido:</h4>
              <ul className="legal-list">
                <li>Debatir ideas y opiniones de manera civilizada</li>
                <li>Expresar desacuerdo de forma respetuosa y fundamentada</li>
                <li>Compartir críticas constructivas con argumentos</li>
                <li>Discutir temas controvertidos con madurez</li>
                <li>Usar humor apropiado que no ofenda ni menosprecie</li>
              </ul>
            </div>

            <h3>2.2. Comportamiento Inaceptable</h3>
            <div className="warning-box">
              <h4>❌ NO está permitido:</h4>
              <ul className="legal-list">
                <li><strong>Discursos de odio:</strong> Contenido que promueva odio o discriminación basado en 
                    raza, etnia, nacionalidad, religión, género, orientación sexual, discapacidad, o cualquier 
                    otra característica protegida</li>
                <li><strong>Acoso y hostigamiento:</strong> Ataques personales, intimidación, seguimiento no deseado, 
                    o comportamiento dirigido a causar angustia</li>
                <li><strong>Insultos y lenguaje ofensivo:</strong> Ataques ad hominem, insultos directos, 
                    lenguaje vulgar dirigido a otros usuarios</li>
                <li><strong>Doxxing:</strong> Compartir información personal de otros sin su consentimiento</li>
                <li><strong>Trolling:</strong> Provocación deliberada, comentarios incendiarios con el único 
                    propósito de generar conflicto</li>
              </ul>
            </div>
          </section>

          <section id="contenido-prohibido" className="legal-section">
            <h2>3. Contenido Prohibido</h2>
            
            <div className="warning-box">
              <p><strong>El siguiente contenido está estrictamente prohibido y resultará en acción inmediata:</strong></p>
            </div>

            <h3>3.1. Contenido Ilegal</h3>
            <ul className="legal-list">
              <li>Cualquier actividad ilegal o que promueva actividades ilegales</li>
              <li>Contenido que infrinja derechos de propiedad intelectual</li>
              <li>Distribución de malware, virus o software malicioso</li>
              <li>Phishing, estafas o fraudes</li>
              <li>Venta de productos ilegales o regulados</li>
            </ul>

            <h3>3.2. Contenido Dañino</h3>
            <ul className="legal-list">
              <li><strong>Violencia explícita:</strong> Contenido gráfico de violencia real, gore, mutilación</li>
              <li><strong>Explotación de menores:</strong> Cualquier contenido que sexualice, explote o ponga 
                  en riesgo a menores de edad</li>
              <li><strong>Autolesión:</strong> Contenido que promueva, glorifique o instruya sobre autolesión, 
                  suicidio o trastornos alimenticios</li>
              <li><strong>Contenido sexual explícito:</strong> Pornografía, desnudez no artística, contenido 
                  sexual gráfico</li>
              <li><strong>Desinformación peligrosa:</strong> Información falsa sobre salud pública, emergencias, 
                  o que pueda causar daño físico</li>
            </ul>

            <h3>3.3. Contenido Sensible (Requiere Contexto)</h3>
            <div className="info-box">
              <p>
                <strong>Contenido sensible puede ser permitido con contexto apropiado:</strong> Discusiones 
                educativas, artísticas, científicas o periodísticas sobre temas sensibles pueden ser aceptables 
                si se manejan con responsabilidad y advertencias apropiadas.
              </p>
              <ul className="legal-list">
                <li>Violencia en contexto histórico, educativo o periodístico</li>
                <li>Desnudez artística o educativa (anatomía, arte)</li>
                <li>Discusión madura sobre salud mental (sin promover autolesión)</li>
              </ul>
            </div>
          </section>

          <section id="autenticidad" className="legal-section">
            <h2>4. Autenticidad e Integridad</h2>
            
            <h3>4.1. Identidad Auténtica</h3>
            <p>
              No es necesario usar tu nombre real, pero tu cuenta debe representar una identidad consistente:
            </p>
            <ul className="legal-list">
              <li><strong>Prohibido:</strong> Hacerse pasar por otra persona real (suplantación de identidad)</li>
              <li><strong>Prohibido:</strong> Crear múltiples cuentas para evadir restricciones o manipular sistemas</li>
              <li><strong>Prohibido:</strong> Usar bots no autorizados o automatización para acciones masivas</li>
              <li><strong>Permitido:</strong> Pseudónimos, nombres artísticos o profesionales</li>
              <li><strong>Permitido:</strong> Cuentas institucionales u organizacionales (claramente identificadas)</li>
            </ul>

            <h3>4.2. Información Veraz</h3>
            <ul className="legal-list">
              <li>No publiques información intencionalmente falsa o engañosa</li>
              <li>No manipules contexto de imágenes, videos o citas para engañar</li>
              <li>Distingue claramente entre hechos, opiniones y especulaciones</li>
              <li>Cita fuentes cuando compartas información factual</li>
              <li>Corrige errores cuando sean señalados de buena fe</li>
            </ul>
          </section>

          <section id="propiedad" className="legal-section">
            <h2>5. Respeto por la Propiedad Intelectual</h2>
            
            <h3>5.1. Contenido Original y Atribución</h3>
            <div className="info-box">
              <p><strong>Prioriza compartir contenido original:</strong></p>
              <ul className="legal-list">
                <li>Crea y comparte tu propio contenido</li>
                <li>Si compartes trabajo de otros, atribuye claramente al creador original</li>
                <li>Proporciona enlaces a fuentes originales cuando sea posible</li>
                <li>No reclames autoría de contenido que no creaste</li>
              </ul>
            </div>

            <h3>5.2. Uso Justo y Derechos de Autor</h3>
            <ul className="legal-list">
              <li>Respeta los derechos de autor de creadores y artistas</li>
              <li>Usa material protegido solo bajo doctrinas de uso justo (educación, crítica, parodia)</li>
              <li>No distribuyas copias no autorizadas de contenido comercial</li>
              <li>Responde a avisos de remoción por infracción de copyright de buena fe</li>
            </ul>
          </section>

          <section id="spam" className="legal-section">
            <h2>6. Spam y Comportamiento Manipulativo</h2>
            
            <div className="warning-box">
              <h4>❌ Spam incluye:</h4>
              <ul className="legal-list">
                <li><strong>Publicaciones repetitivas:</strong> Publicar el mismo contenido múltiples veces</li>
                <li><strong>Promoción excesiva:</strong> Publicidad agresiva de productos, servicios o sitios externos</li>
                <li><strong>Contenido sin valor:</strong> Publicaciones genéricas, sin sentido o irrelevantes</li>
                <li><strong>Clickbait engañoso:</strong> Títulos sensacionalistas que no coinciden con el contenido</li>
                <li><strong>Manipulación de engagement:</strong> Pedir likes, compartir, follows de forma artificial</li>
                <li><strong>Links maliciosos:</strong> Enlaces que redirigen a sitios peligrosos o no relacionados</li>
              </ul>
            </div>

            <h3>6.1. Autopromoción Aceptable</h3>
            <div className="info-box">
              <p>
                <strong>Puedes compartir tu trabajo si:</strong>
              </p>
              <ul className="legal-list">
                <li>Aportas valor genuino a la comunidad</li>
                <li>Participas activamente más allá de autopromoción</li>
                <li>No es tu única actividad en la plataforma</li>
                <li>Es relevante para la comunidad o categoría</li>
              </ul>
            </div>
          </section>

          <section id="seguridad" className="legal-section">
            <h2>7. Seguridad de la Plataforma</h2>
            
            <h3>7.1. Uso Responsable</h3>
            <ul className="legal-list">
              <li>No intentes obtener acceso no autorizado a cuentas o sistemas</li>
              <li>No explotes vulnerabilidades de seguridad (repórtalas responsablemente)</li>
              <li>No uses herramientas automatizadas para scraping masivo de datos</li>
              <li>No sobrecargues intencionalmente nuestros servidores</li>
              <li>No eludas medidas de seguridad o moderación</li>
            </ul>

            <h3>7.2. Protege tu Cuenta</h3>
            <div className="info-box">
              <h4>🔐 Recomendaciones de seguridad:</h4>
              <ul className="legal-list">
                <li>Usa una contraseña fuerte y única</li>
                <li>No compartas tus credenciales con nadie</li>
                <li>Reporta actividad sospechosa en tu cuenta</li>
                <li>Ten cuidado con enlaces y archivos de fuentes desconocidas</li>
              </ul>
            </div>
          </section>

          <section id="moderacion" className="legal-section">
            <h2>8. Moderación y Cumplimiento</h2>
            
            <h3>8.1. Enfoque de Moderación</h3>
            <p>
              Nuestro equipo de moderación trabaja para mantener la calidad y seguridad de la comunidad:
            </p>
            <ul className="legal-list">
              <li><strong>Revisión humana:</strong> Las decisiones importantes son revisadas por moderadores humanos</li>
              <li><strong>Contexto importa:</strong> Evaluamos el contexto y la intención, no solo palabras clave</li>
              <li><strong>Graduación de respuestas:</strong> Las consecuencias son proporcionales a la infracción</li>
              <li><strong>Oportunidad de mejora:</strong> Priorizamos educación y corrección sobre castigos permanentes</li>
            </ul>

            <h3>8.2. Tipos de Acción Moderadora</h3>
            <div className="definition-list">
              <div className="definition-item">
                <strong>⚠️ Advertencia</strong>
                <p>
                  Notificación sobre contenido que viola normas. El contenido puede ser removido. 
                  Sin impacto en la cuenta si se corrige el comportamiento.
                </p>
              </div>
              <div className="definition-item">
                <strong>⏸️ Suspensión Temporal</strong>
                <p>
                  Restricción de funcionalidades por período determinado (1-30 días). Aplica para infracciones 
                  repetidas o moderadas.
                </p>
              </div>
              <div className="definition-item">
                <strong>🚫 Suspensión de Cuenta</strong>
                <p>
                  Desactivación permanente de la cuenta por infracciones graves o repetidas tras suspensiones previas.
                </p>
              </div>
              <div className="definition-item">
                <strong>⚖️ Acción Legal</strong>
                <p>
                  Para contenido ilegal grave (explotación infantil, amenazas creíbles, etc.), podemos 
                  cooperar con autoridades legales.
                </p>
              </div>
            </div>
          </section>

          <section id="reportar" className="legal-section">
            <h2>9. Reportar Infracciones</h2>
            
            <h3>9.1. Cómo Reportar</h3>
            <p>
              Si observas contenido o comportamiento que viola estas normas, ayúdanos a mantener la comunidad segura:
            </p>
            <div className="info-box">
              <h4>📢 Opciones para reportar:</h4>
              <ul className="legal-list">
                <li><strong>Botón de reporte:</strong> Disponible en cada publicación y perfil</li>
                <li><strong>Correo de moderación:</strong> admin.graphyra@gmail.com</li>
                <li><strong>Formulario de contacto:</strong> <Link to="/contact">Página de contacto</Link></li>
              </ul>
            </div>

            <h3>9.2. Qué Incluir en un Reporte</h3>
            <ul className="legal-list">
              <li>Enlace directo al contenido o perfil problemático</li>
              <li>Descripción específica de la violación</li>
              <li>Contexto adicional relevante (si aplica)</li>
              <li>Capturas de pantalla (para contenido que puede ser eliminado rápidamente)</li>
            </ul>

            <h3>9.3. Reportes de Buena Fe</h3>
            <div className="info-box">
              <p>
                <strong>Reporta responsablemente:</strong> Los reportes falsos o maliciosos con intención 
                de acosar o censurar a otros pueden resultar en acciones contra tu propia cuenta.
              </p>
            </div>
          </section>

          <section id="consecuencias" className="legal-section">
            <h2>10. Consecuencias y Apelaciones</h2>
            
            <h3>10.1. Sistema de Consecuencias Graduales</h3>
            <div className="definition-list">
              <div className="definition-item">
                <strong>Primera infracción menor</strong>
                <p>Advertencia + Remoción de contenido. Sin impacto adicional si se corrige.</p>
              </div>
              <div className="definition-item">
                <strong>Segunda infracción o infracción moderada</strong>
                <p>Suspensión temporal 3-7 días + Remoción de contenido.</p>
              </div>
              <div className="definition-item">
                <strong>Tercera infracción o infracción grave</strong>
                <p>Suspensión temporal extendida 14-30 días + Revisión de historial.</p>
              </div>
              <div className="definition-item">
                <strong>Infracciones reiteradas o muy graves</strong>
                <p>Suspensión permanente de cuenta. Infracciones extremas (contenido ilegal, amenazas creíbles) 
                pueden resultar en suspensión inmediata sin advertencias previas.</p>
              </div>
            </div>

            <h3>10.2. Proceso de Apelación</h3>
            <p>
              Si crees que una decisión de moderación fue incorrecta, puedes apelar:
            </p>
            <ol className="legal-list" style={{ listStyle: 'decimal', paddingLeft: '2rem' }}>
              <li>Envía un correo a <strong>admin.graphyra@gmail.com</strong> con asunto "Apelación de Moderación"</li>
              <li>Incluye tu nombre de usuario y detalles de la acción de moderación</li>
              <li>Explica por qué crees que la decisión fue incorrecta</li>
              <li>Proporciona contexto o evidencia relevante</li>
            </ol>
            <p className="legal-note">
              <strong>Plazo de respuesta:</strong> Revisaremos apelaciones dentro de 5-10 días hábiles. 
              Las decisiones tras apelación son finales.
            </p>

            <h3>10.3. Contacto de Moderación</h3>
            <div className="contact-box">
              <p><strong>📧 Equipo de moderación:</strong> admin.graphyra@gmail.com</p>
              <p><strong>📧 Soporte general:</strong> support.graphyra@gmail.com</p>
              <p><strong>📞 Teléfono:</strong> +57 3115491733</p>
              <p><strong>🌐 Formulario:</strong> <Link to="/contact">Página de contacto</Link></p>
            </div>
          </section>

          <div className="legal-footer">
            <p>
              Estas Normas de la Comunidad complementan nuestros Términos y Condiciones. Al usar Graphyra, 
              aceptas cumplir con estas normas y contribuir a mantener una comunidad positiva.
            </p>
            <div className="legal-links">
              <Link to="/terms">Términos y Condiciones</Link>
              <span className="separator">•</span>
              <Link to="/privacy">Política de Privacidad</Link>
              <span className="separator">•</span>
              <Link to="/">Volver al inicio</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Guidelines;
